import { useEffect, useState } from "react";
import {
  listPrograms,
  listSessions,
  createSession,
  deleteSession,
  updateSession,
  listStaff,
  listProgramsByCoachAdmin,
} from "../../../apis/programsApi";
import {
  Box,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Button,
  Alert,
  Snackbar,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { SessionCard } from "../../../components/admin/programs/SessionCard";
import { SessionDialog } from "../../../components/admin/programs/SessionDialog";
import { DeleteSessionDialog } from "../../../components/admin/programs/DeleteSessionDialog";
import { getDefaultSessionCapacity } from "../../../utils/programTimeSlotUtils";

const ManageProgramSessions = () => {
  const [programs, setPrograms] = useState<any[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const [sessions, setSessions] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [form, setForm] = useState<any>({
    name: "",
    startDate: "",
    endDate: "",
    capacity: getDefaultSessionCapacity("hourly"),
    isActive: true,
    ageGroups: [],
    sessionSlotType: "hourly",
    imageUrl: "",
    slotAgeGroups: {},
    slotCapacities: {},
    imageFile: undefined,
    dayOfWeekTimes: {},
    dayOfWeekDateRanges: {},
    dateEndOverrides: {},
    holidays: [],
    extraDates: [],
  });
  const [usePerDayDates, setUsePerDayDates] = useState(false);
  const [usePerDayTimes, setUsePerDayTimes] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [staff, setStaff] = useState<Array<{ id: string; email: string; name?: string; role: string; firstName?: string; lastName?: string }>>([]);
  
  // Get user type for role-based restrictions
  const userType = localStorage.getItem("userType");
  const canEdit = ["admin", "employee", "employee_admin", "coach_admin"].includes(userType || "");

  useEffect(() => {
    (async () => {
      let p
      if (["admin", "employee", "employee_admin"].includes(userType || "")) {
        p = await listPrograms();
      }
      else {
        p = await listProgramsByCoachAdmin(localStorage.getItem("userId") || "");
      }
      setPrograms(p);
      if (p.length > 0) setSelectedProgramId(p[0].id);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const s = await listStaff(["coach", "coach_employee", "employee"]);
        setStaff(s);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!selectedProgramId) return;
      const s = await listSessions(selectedProgramId);
      setSessions(s);
    })();
  }, [selectedProgramId]);

  const onChange = (e: any) => {
    const { name, value } = e.target;
    const processedValue = name === "isActive" ? value === "true" : value;

    // Match main CRUD flow; only apply default session capacity when creating (not editing).
    if (name === "sessionSlotType" && !editingId) {
      setForm({
        ...form,
        sessionSlotType: processedValue,
        capacity: getDefaultSessionCapacity(processedValue),
      });
      return;
    }

    setForm({ ...form, [name]: processedValue });
  };

  const resetForm = () => {
    setForm({
      name: "",
      startDate: "",
      endDate: "",
      capacity: getDefaultSessionCapacity("hourly"),
      isActive: true,
      ageGroups: [],
      sessionSlotType: "hourly",
      slotCapacityGranularity: "hourly",
      imageUrl: "",
      slotAgeGroups: {},
      slotCapacities: {},
      dayOfWeekTimes: {},
      dayOfWeekDateRanges: {},
      dateEndOverrides: {},
      holidays: [],
      extraDates: [],
      imageFile: undefined,
    });
    setUsePerDayDates(false);
    setUsePerDayTimes(false);
    setEditingId(null);
  };

  const onCreate = async () => {
    try {
      await createSession(selectedProgramId, {
        ...form,
        capacity: Number(form.capacity),
      });
      resetForm();
      setDialogOpen(false);
      const s = await listSessions(selectedProgramId);
      setSessions(s);
      setSnackbar({
        open: true,
        message: "Session created successfully!",
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response.data.message || "Failed to create session",
        severity: "error",
      });
    }
  };

  const onUpdate = async () => {
    if (!editingId) return;
    try {
      await updateSession(editingId, {
        ...form,
        capacity: Number(form.capacity),
      });
      resetForm();
      setDialogOpen(false);
      const s = await listSessions(selectedProgramId);
      setSessions(s);
      setSnackbar({
        open: true,
        message: "Session updated successfully!",
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response.data.message || "Failed to update session",
        severity: "error",
      });
    }
  };

  const formatDateTimeLocal = (d?: string | Date) => {
    if (!d) return "";
    const dt = typeof d === "string" ? new Date(d) : d;
    if (isNaN(dt.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
      dt.getDate()
    )}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
  };

  const onEdit = (s: any) => {
    setEditingId(s.id || s.sessionId || null);
    setUsePerDayDates(
      Boolean(
        s.dayOfWeekDateRanges && Object.keys(s.dayOfWeekDateRanges).length
      )
    );
    setUsePerDayTimes(
      Boolean(s.dayOfWeekTimes && Object.keys(s.dayOfWeekTimes).length)
    );
    setForm({
      name: s.name || "",
      startDate: formatDateTimeLocal(s.startDate),
      endDate: formatDateTimeLocal(s.endDate),
      capacity: s.capacity ?? getDefaultSessionCapacity(s.sessionSlotType || "hourly"),
      isActive: (s as any).isActive ?? true,
      sessionSlotType: s.sessionSlotType || "hourly",
      slotCapacityGranularity: (s as any).slotCapacityGranularity || "hourly",
      imageUrl: s.imageUrl || "",
      ageGroups: s.ageGroups || [],
      slotAgeGroups: s.slotAgeGroups || {},
      slotCapacities: s.slotCapacities || {},
      dayOfWeekTimes: s.dayOfWeekTimes || {},
      dayOfWeekDateRanges: s.dayOfWeekDateRanges || {},
      dateEndOverrides: s.dateEndOverrides || {},
      holidays: s.holidays || [],
      extraDates: s.extraDates || [],
      imageFile: undefined,
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setSessionToDelete(id);
    setDeleteDialogOpen(true);
  };

  const onDelete = async () => {
    if (sessionToDelete) {
      try {
        await deleteSession(sessionToDelete);
        const s = await listSessions(selectedProgramId);
        setSessions(s);
        setSnackbar({
          open: true,
          message: "Session deleted successfully!",
          severity: "success",
        });
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || "Failed to delete session",
          severity: "error",
        });
      }
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    }
  };

  const refreshSessions = async () => {
    if (selectedProgramId) {
      try {
        const s = await listSessions(selectedProgramId);
        setSessions(s);
        setSnackbar({
          open: true,
          message: "Sessions refreshed successfully!",
          severity: "success",
        });
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error.response.data.message || "Failed to refresh sessions",
          severity: "error",
        });
      }
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", sm: "column", md: "row" }}
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Manage Sessions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage training sessions for your programs
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="large"
          startIcon={<RefreshIcon />}
          onClick={refreshSessions}
          disabled={!selectedProgramId}
          sx={{ borderRadius: 2, px: 3 }}
        >
          Refresh Sessions
        </Button>
      </Stack>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack
          sx={{ mx: { xs: 0, sm: 0, md: 2, p: { xs: 0, sm: 0, md: 2 } } }}
            direction={{ xs: "column", sm: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <TextField
              select
              label="Select Program"
              value={selectedProgramId}
              onChange={(e) => setSelectedProgramId(e.target.value)}
              sx={{ minWidth: 300 }}
            >
              {programs.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.title}
                </MenuItem>
              ))}
            </TextField>
            {canEdit && (
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                disabled={!selectedProgramId}
                sx={{ borderRadius: 2, px: 3 }}
              >
                Create Session
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {sessions.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", borderRadius: 3 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No sessions yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {selectedProgramId
              ? "Get started by creating your first session"
              : "Please select a program first"}
          </Typography>
          {selectedProgramId && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
            >
              Create Your First Session
            </Button>
          )}
        </Paper>
      ) : (
        <Stack
          direction={{ xs: "column", sm: "column", md: "row" }}
          spacing={{ xs: 1, sm: 2, md: 4 }}
        >
          {sessions.map((s) => (
            <SessionCard
              key={s.id || s.sessionId}
              session={s}
              onEdit={onEdit}
              onDelete={handleDeleteClick}
              canEdit={canEdit}
              onToggleActive={async (id: string, current: boolean) => {
                try {
                  await updateSession(id, { isActive: !current });
                  const refreshed = await listSessions(selectedProgramId);
                  setSessions(refreshed);
                  setSnackbar({
                    open: true,
                    message: `Session ${current ? "deactivated" : "activated"} successfully!`,
                    severity: "success",
                  });
                } catch (error: any) {
                  setSnackbar({
                    open: true,
                    message: error?.response?.data?.message || "Failed to update session status",
                    severity: "error",
                  });
                }
              }}
            />
          ))}
        </Stack>
      )}

      {/* Create/Edit Dialog */}
      {canEdit && (
        <SessionDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          resetForm();
        }}
        onSubmit={editingId ? onUpdate : onCreate}
        form={form}
        onChange={onChange}
        setForm={setForm}
        editingId={editingId}
        selectedProgramId={selectedProgramId}
        programs={programs}
        staff={staff}
        usePerDayDates={usePerDayDates}
        setUsePerDayDates={setUsePerDayDates}
        usePerDayTimes={usePerDayTimes}
        setUsePerDayTimes={setUsePerDayTimes}
      />
      )}

      {/* Delete Confirmation Dialog */}
      {canEdit && (
        <DeleteSessionDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={onDelete}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageProgramSessions;
